import React, { useState, useEffect, useCallback } from 'react';
import { format, isValid } from 'date-fns';
import { 
  Search, Calendar, Plus, Eye, Pencil, Trash, 
  Clock, MapPin, Globe, Filter, X, Check, RefreshCw, ChevronDown, Loader2
} from 'lucide-react'; 
import { GET, POST, PUT, DELETE } from '../services/api';
import { useMasterChapter, MasterChapterLockNote } from '../context/MasterChapter';

// --- Global Styles for Thin Scrollbar & Animations ---
const CustomStyles = () => (
  <style>{`
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 8px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: #cbd5e1;
      border-radius: 8px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background-color: #94a3b8;
    }
    @keyframes fade-in-up {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in-up {
      animation: fade-in-up 0.4s ease-out forwards;
    }
  `}</style>
);

// --- Toast Component ---
const Toast = ({ message, type, onClose }) => {
  if (!message) return null;
  const bgColor = type === 'success' ? 'bg-emerald-600' : 'bg-red-500';
  return (
    <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5 ${bgColor}`}>
      {type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
      {message}
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// --- Confirmation Modal ---
const ConfirmationModal = ({ title, message, onConfirm, onCancel, isOpen }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[999] p-4 animate-in fade-in">
      <div className="bg-white p-6 rounded-3xl shadow-2xl max-w-sm w-full text-center border border-slate-100 scale-100 animate-in zoom-in-95">
        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-red-50">
            <Trash className="w-6 h-6" />
        </div>
        <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
        <p className="text-slate-500 mb-6 font-medium">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 font-bold transition-colors">Cancel</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-bold transition-colors shadow-lg shadow-red-200">Confirm</button>
        </div>
      </div>
    </div>
  );
};

// --- Helper Functions (Defined Globally to fix Reference Errors) ---
const formatDateForDisplay = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? 'Invalid Date' : format(date, 'MMM dd, yyyy');
};

// ** FIX: This function was missing or undefined in your previous error **
const formatTimeForDisplay = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return isNaN(date.getTime()) ? 'Invalid Time' : format(date, 'hh:mm a');
};

const formatTimeRange = (start, end) => {
    if (!start || !end) return "Time N/A";
    const startParsed = new Date(start);
    const endParsed = new Date(end);
    if (!isValid(startParsed) || !isValid(endParsed)) return "Invalid time";
    return `${format(startParsed, 'hh:mm a')} - ${format(endParsed, 'hh:mm a')}`;
};

const getEventId = (event) => event?._id || event?.id || '';

// =================================================================
// MAIN COMPONENT: EVENTS
// =================================================================
const Events = () => {
    // State management
    const [isLoading, setIsLoading] = useState(true); // ** NEW: Loading State **
    const [showScheduleForm, setShowScheduleForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [viewEvent, setViewEvent] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ city: '', chapter: '', dateFrom: '', dateTo: '' });
    const master = useMasterChapter();

    // Event form data
    const [eventData, setEventData] = useState({
        title: '', subtitle: '', description: '', startDate: '', endDate: '', chapter_id: '',
        location: { venue: '', address: '', city: '', latitude: '', longitude: '' }
    });

    // API Data states
    const [events, setEvents] = useState([]);
    const [cities, setCities] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [filterChapters, setFilterChapters] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [toast, setToast] = useState({ message: '', type: '', id: null });
    const [confirmation, setConfirmation] = useState({ isOpen: false, title: '', message: '', onConfirm: () => { }, onCancel: () => { } });

    // Toast Helper
    const showToast = useCallback((message, type = 'info', duration = 3000) => {
        if (toast.id) clearTimeout(toast.id);
        const newId = setTimeout(() => setToast({ message: '', type: '', id: null }), duration);
        setToast({ message, type, id: newId });
    }, [toast.id]);

    // Confirmation Helper
    const showConfirmation = useCallback((title, message, onConfirmCallback) => {
        setConfirmation({
            isOpen: true, title, message,
            onConfirm: () => { onConfirmCallback(); setConfirmation(prev => ({ ...prev, isOpen: false })); },
            onCancel: () => { setConfirmation(prev => ({ ...prev, isOpen: false })); }
        });
    }, []);

    // API Calls
    const fetchCities = useCallback(async () => {
        try { const data = await GET('/events/cities'); if (data) setCities(data); } 
        catch (error) { console.error('Error fetching cities:', error); }
    }, []);

    const fetchChaptersByCity = useCallback(async (cityName) => {
        if (!cityName) { setChapters([]); return; }
        try { const data = await GET(`/events/chapters/city/${cityName}`); setChapters(data || []); } 
        catch (error) { console.error('Error fetching chapters:', error); setChapters([]); }
    }, []);

    const fetchEvents = useCallback(async () => {
        setIsLoading(true); // Start loading
        try { 
            const query = filters.chapter ? `&chapterId=${encodeURIComponent(filters.chapter)}` : '';
            const responseData = await GET(`/events?limit=1000${query}`);
            const eventList = responseData?.data || (Array.isArray(responseData) ? responseData : []);
            const normalizedEvents = Array.isArray(eventList)
                ? eventList.map((evt) => ({ ...evt, _id: evt?._id || evt?.id || '' }))
                : [];
            setEvents(normalizedEvents);
        } catch (error) { 
            console.error("Error fetching events:", error); 
            showToast('Failed to fetch events.', 'error'); 
        } finally {
            // Stop loading after a short delay for smoothness, or immediately
            setTimeout(() => setIsLoading(false), 500); 
        }
    }, [showToast, filters.chapter]);

    useEffect(() => { fetchEvents(); }, [fetchEvents]);
    useEffect(() => { fetchCities(); }, [fetchCities]);

    // Chapter filter options follow the selected filter city (cities are matched by name)
    useEffect(() => {
        if (!filters.city) { setFilterChapters([]); return; }
        GET(`/events/chapters/city/${encodeURIComponent(filters.city)}`)
            .then(data => setFilterChapters(data || []))
            .catch(() => setFilterChapters([]));
    }, [filters.city]);

    // Super-admin master chapter filter locks the city/chapter filter; clearing it resets to "All"
    useEffect(() => {
        setFilters(prev => ({ ...prev, city: master.cityName, chapter: master.chapterId }));
        setCurrentPage(1);
    }, [master.chapterId, master.cityName]);

    // Filter Logic
    const filteredEvents = events.filter(event => {
        const matchesSearch = !searchTerm || event.title.toLowerCase().includes(searchTerm.toLowerCase()) || (event.location?.city && event.location.city.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCity = !filters.city || event.location.city === filters.city;
        const eventDate = event.startDate ? new Date(event.startDate) : null;
        const filterDateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
        const filterDateTo = filters.dateTo ? new Date(filters.dateTo) : null;
        if (filterDateFrom) filterDateFrom.setHours(0, 0, 0, 0);
        if (filterDateTo) filterDateTo.setHours(23, 59, 59, 999);
        const matchesDateFrom = !filterDateFrom || (eventDate && eventDate >= filterDateFrom);
        const matchesDateTo = !filterDateTo || (eventDate && eventDate <= filterDateTo);
        return matchesSearch && matchesCity && matchesDateFrom && matchesDateTo;
    });

    const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedEvents = filteredEvents.slice(startIndex, startIndex + itemsPerPage);

    // Handlers
    const handleEventInputChange = (field, value) => {
        if (field.startsWith('location.')) {
            const locationField = field.split('.')[1];
            setEventData({ ...eventData, location: { ...eventData.location, [locationField]: value } });
            if (locationField === 'city') {
                fetchChaptersByCity(value);
                setEventData(prev => ({ ...prev, chapter_id: '' }));
            }
        } else {
            setEventData({ ...eventData, [field]: value });
        }
        if (formErrors[field]) setFormErrors({ ...formErrors, [field]: '' });
    };

    const validateEventForm = () => {
        const errors = {};
        if (!eventData.title.trim()) errors.title = 'Title required';
        if (!eventData.description.trim()) errors.description = 'Description required';
        if (!eventData.startDate) errors.startDate = 'Start date required';
        if (!eventData.endDate) errors.endDate = 'End date required';
        if (!eventData.location.city) errors['location.city'] = 'City required';
        if (!eventData.location.address.trim()) errors['location.address'] = 'Address required';
        if (eventData.startDate && eventData.endDate && new Date(eventData.endDate) <= new Date(eventData.startDate)) {
            errors.endDate = 'End date must be after start date.';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleScheduleEvent = async () => {
        if (validateEventForm()) {
            showConfirmation(editingEvent ? 'Update Event' : 'Schedule Event', `Are you sure you want to ${editingEvent ? 'update' : 'schedule'} this event?`, async () => {
                try {
                    const editId = getEventId(editingEvent);
                    const result = editingEvent ? await PUT(`/events/${editId}`, eventData) : await POST('/events', eventData);
                    if (!result) throw new Error("No response");
                    showToast(result.message || `Event ${editingEvent ? 'updated' : 'scheduled'} successfully!`, 'success');
                    setShowScheduleForm(false); setEditingEvent(null);
                    setEventData({ title: '', subtitle: '', description: '', startDate: '', endDate: '', chapter_id: '', location: { venue: '', address: '', city: '', latitude: '', longitude: '' } });
                    fetchEvents(); 
                } catch (error) { showToast(`Failed: ${error.message}`, 'error'); }
            });
        }
    };

    const handleDeleteEvent = (eventId) => {
        if (!eventId) {
            showToast('Event ID missing. Please refresh and try again.', 'error');
            return;
        }
        showConfirmation('Delete Event', 'Are you sure? This cannot be undone.', async () => {
            try { await DELETE(`/events/${eventId}`); showToast('Deleted successfully!', 'success'); fetchEvents(); } 
            catch (error) { showToast(`Failed: ${error.message}`, 'error'); }
        });
    };

    const handleEditEvent = (event) => {
        setEditingEvent(event);
        const city = event.location?.city || '';    
        setEventData({
            title: event.title, subtitle: event.subtitle || '', description: event.description || '',
            chapter_id: event.chapter_id?._id || event.chapter_id || '',
            startDate: event.startDate ? new Date(event.startDate).toISOString().slice(0, 16) : '',
            endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 16) : '',
            location: {
                venue: event.location?.venue || '', address: event.location?.address || '', city: city,
                latitude: event.location?.latitude || '', longitude: event.location?.longitude || ''
            }
        });
        if (city) fetchChaptersByCity(city);
        setShowScheduleForm(true);
    };

    const resetFilters = () => {
        setFilters({ city: master.cityName, chapter: master.chapterId, dateFrom: '', dateTo: '' }); setSearchTerm(''); setCurrentPage(1);
        showToast('Filters reset', 'success');
    };

    const handleCreateClick = () => {
        setEditingEvent(null); 
        setShowScheduleForm(true); 
        // Super-admin: pre-fill the master chapter (still editable)
        setEventData({ title: '', subtitle: '', description: '', startDate: '', endDate: '', chapter_id: master.chapterId, location: { venue: '', address: '', city: master.cityName, latitude: '', longitude: '' } });
        fetchChaptersByCity(master.cityName); 
        setFormErrors({});
    }

    // --- Loading State Render ---
    if (isLoading) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center">
                <CustomStyles />
                <div className="flex flex-col items-center animate-pulse">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                    <p className="text-slate-500 font-bold text-lg">Loading Events...</p>
                    <p className="text-slate-400 text-sm">Please wait while we fetch the latest data.</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <CustomStyles />
            
            {/* Added margin-top to clear the fixed sidebar/header */}
            <div className="space-y-5">

                {/* --- Filter Section with Add Button --- */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 animate-fade-in-up">
                    {/* Header: Title Left, Add Button Right */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-slate-100">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <Filter className="w-5 h-5" />
                            </div>
                            <h2 className="text-base font-bold text-slate-800 uppercase tracking-wide">Filter Events</h2>
                        </div>
                        <button 
                            onClick={handleCreateClick} 
                            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all flex items-center gap-2"
                        >
                            <Plus className="w-5 h-5" />
                            <span>Create Event</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-5 gap-5 items-end">
                        <div className="relative group">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Search</label>
                            <div className="relative">
                                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search title or city..." className="w-full p-2.5 pl-10 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium text-slate-700" />
                                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3.5" />
                            </div>
                        </div>
                        <div className="relative group">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">City</label>
                            <div className="relative">
                                <select value={filters.city} disabled={!!master.chapterId} onChange={(e) => { setFilters({ ...filters, city: e.target.value, chapter: '' }); setCurrentPage(1); }} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 disabled:opacity-60 disabled:cursor-not-allowed">
                                    <option value="">All Cities</option>
                                    {cities.map(city => <option key={city._id} value={city.name}>{city.name}</option>)}
                                </select>
                                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
                            </div>
                        </div>
                        <div className="relative group">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">Chapter</label>
                            <div className="relative">
                                <select value={filters.chapter} disabled={!!master.chapterId || !filters.city} onChange={(e) => { setFilters({ ...filters, chapter: e.target.value }); setCurrentPage(1); }} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700 disabled:opacity-60 disabled:cursor-not-allowed">
                                    <option value="">All Chapters</option>
                                    {filterChapters.map(ch => <option key={ch._id} value={ch._id}>{ch.name}</option>)}
                                </select>
                                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">From</label>
                            <input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700" />
                        </div>
                        <div className="flex gap-2">
                             <div className="flex-1">
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1">To</label>
                                <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700" />
                             </div>
                             <button onClick={resetFilters} className="px-4 py-2.5 mt-6 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm" title="Reset Filters"><RefreshCw className="w-5 h-5" /></button>
                        </div>
                    </div>
                    <div className="mt-3"><MasterChapterLockNote /></div>
                </div>

                {/* Events Table */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                    <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white">
                        <h2 className="text-lg font-bold text-slate-800">Events List</h2>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-500">Rows:</span>
                            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="p-1.5 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-500">
                                <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
                            </select>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                            <thead className="bg-slate-50/50">
                                <tr>
                                    {['SR.', 'Title', 'Date', 'City', 'Chapter', 'Time', 'Actions'].map((h) => (
                                        <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-slate-100">
                                {paginatedEvents.length === 0 ? (
                                    <tr><td colSpan="7" className="px-6 py-10 text-center text-slate-400 font-medium">No events found matching your criteria.</td></tr>
                                ) : (
                                    paginatedEvents.map((event, index) => (
                                        <tr key={getEventId(event) || index} className="hover:bg-slate-50/80 transition-colors group">
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-bold text-slate-500">{startIndex + index + 1}</td>
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-slate-800 text-sm truncate max-w-[200px]">{event.title}</div>
                                                <div className="text-xs text-slate-400 truncate max-w-[200px]">{event.subtitle}</div>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">
                                                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {formatDateForDisplay(event.startDate)}</div>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 text-blue-600 border border-blue-100">{event.location?.city || 'N/A'}</span>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">{event.chapter_id?.name || <span className="text-slate-400">All chapters</span>}</td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium text-slate-600">
                                                <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" /> {formatTimeRange(event.startDate, event.endDate)}</div>
                                            </td>
                                            <td className="px-5 py-3 whitespace-nowrap text-sm font-medium">
                                                <div className="flex gap-2">
                                                    <button onClick={() => setViewEvent(event)} className="p-2 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors" title="View"><Eye className="w-4 h-4" /></button>
                                                    <button onClick={() => handleEditEvent(event)} className="p-2 rounded-lg text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors" title="Edit"><Pencil className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDeleteEvent(getEventId(event))} className="p-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors" title="Delete"><Trash className="w-4 h-4" /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {paginatedEvents.length > 0 && (
                        <div className="px-6 py-4 bg-white border-t border-slate-100 flex items-center justify-between">
                            <div className="text-sm font-medium text-slate-500">
                                Showing <span className="font-bold text-slate-700">{startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredEvents.length)}</span> of <span className="font-bold text-slate-700">{filteredEvents.length}</span>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Previous</button>
                                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm">Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal: Schedule / Edit */}
            {showScheduleForm && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
                    {/* Added custom-scrollbar class for thin scroll */}
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 custom-scrollbar">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><Calendar className="w-5 h-5" /></div>
                                {editingEvent ? 'Edit Event' : 'Create New Event'}
                            </h3>
                            <button onClick={() => setShowScheduleForm(false)} className="p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="col-span-1 md:col-span-2 space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Title *</label>
                                    <input type="text" value={eventData.title} onChange={(e) => handleEventInputChange('title', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800 ${formErrors.title ? 'border-red-500' : 'border-slate-200'}`} placeholder="Event Title" />
                                    {formErrors.title && <p className="text-red-500 text-xs font-bold">{formErrors.title}</p>}
                                </div>
                                <div className="col-span-1 md:col-span-2 space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Subtitle</label>
                                    <input type="text" value={eventData.subtitle} onChange={(e) => handleEventInputChange('subtitle', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800" placeholder="Event Subtitle" />
                                </div>
                                <div className="col-span-1 md:col-span-2 space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Description *</label>
                                    <textarea value={eventData.description} onChange={(e) => handleEventInputChange('description', e.target.value)} rows="3" className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800 ${formErrors.description ? 'border-red-500' : 'border-slate-200'}`} placeholder="Event Details..." />
                                    {formErrors.description && <p className="text-red-500 text-xs font-bold">{formErrors.description}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Start Date *</label>
                                    <input type="datetime-local" value={eventData.startDate} onChange={(e) => handleEventInputChange('startDate', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-600 ${formErrors.startDate ? 'border-red-500' : 'border-slate-200'}`} />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">End Date *</label>
                                    <input type="datetime-local" value={eventData.endDate} onChange={(e) => handleEventInputChange('endDate', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-600 ${formErrors.endDate ? 'border-red-500' : 'border-slate-200'}`} />
                                    {formErrors.endDate && <p className="text-red-500 text-xs font-bold">{formErrors.endDate}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">City *</label>
                                    <select value={eventData.location.city} onChange={(e) => handleEventInputChange('location.city', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 ${formErrors['location.city'] ? 'border-red-500' : 'border-slate-200'}`}>
                                        <option value="">Select City</option>
                                        {cities.map(city => <option key={city._id} value={city.name}>{city.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Chapter</label>
                                    <select value={eventData.chapter_id} onChange={(e) => handleEventInputChange('chapter_id', e.target.value)} disabled={!eventData.location.city} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-700 disabled:opacity-50">
                                        <option value="">All chapters in this city</option>
                                        {chapters.map(chapter => <option key={chapter._id} value={chapter._id}>{chapter.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-span-1 md:col-span-2 space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Address *</label>
                                    <input type="text" value={eventData.location.address} onChange={(e) => handleEventInputChange('location.address', e.target.value)} className={`w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800 ${formErrors['location.address'] ? 'border-red-500' : 'border-slate-200'}`} placeholder="Full Address" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Latitude</label>
                                    <input type="number" step="any" value={eventData.location.latitude} onChange={(e) => handleEventInputChange('location.latitude', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800" placeholder="Optional" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Longitude</label>
                                    <input type="number" step="any" value={eventData.location.longitude} onChange={(e) => handleEventInputChange('location.longitude', e.target.value)} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium text-slate-800" placeholder="Optional" />
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-slate-100 flex gap-3 bg-slate-50/50 sticky bottom-0 z-10">
                            <button onClick={() => setShowScheduleForm(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
                            <button onClick={handleScheduleEvent} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 shadow-lg shadow-blue-200 transition-colors">{editingEvent ? 'Update Event' : 'Create Event'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* View Modal */}
            {viewEvent && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full animate-in zoom-in-95 custom-scrollbar">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 rounded-t-3xl">
                            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <div className="p-2 bg-purple-50 rounded-xl text-purple-600"><Eye className="w-5 h-5" /></div>
                                Event Details
                            </h3>
                            <button onClick={() => setViewEvent(null)} className="p-2 rounded-full hover:bg-white text-slate-400 hover:text-slate-600 transition-colors"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div><h4 className="text-xl font-extrabold text-slate-800">{viewEvent.title}</h4><p className="text-slate-500 font-medium">{viewEvent.subtitle}</p></div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-sm text-slate-600 leading-relaxed">{viewEvent.description}</div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <Calendar className="w-5 h-5 text-blue-500" />
                                    <div><p className="text-xs font-bold text-slate-400 uppercase">Date</p><p className="text-sm font-bold text-slate-700">{formatDateForDisplay(viewEvent.startDate)}</p></div>
                                </div>
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <Clock className="w-5 h-5 text-amber-500" />
                                    <div><p className="text-xs font-bold text-slate-400 uppercase">Time</p><p className="text-sm font-bold text-slate-700">{formatTimeForDisplay(viewEvent.startDate)}</p></div>
                                </div>
                            </div>
                            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <MapPin className="w-5 h-5 text-red-500 mt-0.5" />
                                <div><p className="text-xs font-bold text-slate-400 uppercase">Location</p><p className="text-sm font-bold text-slate-700">{viewEvent.location?.address}, {viewEvent.location?.city}</p></div>
                            </div>
                            {(viewEvent.location?.latitude && viewEvent.location?.longitude) && (
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <Globe className="w-5 h-5 text-emerald-500" />
                                    <div><p className="text-xs font-bold text-slate-400 uppercase">Coordinates</p><p className="text-sm font-bold text-slate-700">{viewEvent.location.latitude}, {viewEvent.location.longitude}</p></div>
                                </div>
                            )}
                        </div>
                        <div className="p-6 border-t border-slate-100 bg-slate-50/50 rounded-b-3xl">
                            <button onClick={() => setViewEvent(null)} className="w-full py-3 bg-white border border-slate-200 text-slate-700 font-bold rounded-xl hover:bg-slate-50 transition-colors shadow-sm">Close</button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: '', id: null })} />
            <ConfirmationModal isOpen={confirmation.isOpen} title={confirmation.title} message={confirmation.message} onConfirm={confirmation.onConfirm} onCancel={confirmation.onCancel} />
        </div>
    );
};

export default Events;